import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash, PencilSimple, SealCheck, Clock, XCircle, ArrowSquareOut, Upload } from '@phosphor-icons/react';
import { parseInrInput, formatInrFriendly } from '../lib/inr';
import Modal from '../components/Modal';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INDUSTRIES = ['Tollywood', 'Bollywood', 'Kollywood', 'Hollywood'];
const ROLES = ['Producer', 'Co-Producer', 'Director', 'Executive Producer', 'Writer'];
const DISTRIBUTION = ['Theatrical', 'OTT', 'Hybrid', 'Festival'];

const emptyProject = {
  title: '',
  role: 'Producer',
  industry: 'Bollywood',
  budget_inr: '',
  box_office_inr: '',
  release_date: '',
  planned_release_date: '',
  distribution: 'Theatrical',
  platform: '',
  imdb_url: '',
  trailer_url: '',
  poster_url: '',
  proof_doc_url: '',
  notes: '',
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: { label: 'Pending verification', color: 'bg-amber-500/10 text-amber-300 border-amber-500/30', Icon: Clock },
    verified: { label: 'Verified', color: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30', Icon: SealCheck },
    rejected: { label: 'Rejected', color: 'bg-rose-500/10 text-rose-300 border-rose-500/30', Icon: XCircle },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-medium border ${cfg.color}`} data-testid={`project-status-${status}`}>
      <Icon size={12} weight="bold" /> {cfg.label}
    </span>
  );
};

const ProducerTrackRecord = ({ user }) => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({ company_name: '', bio: '', location: '', website: '', years_active: '' });
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyProject);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    load();
  }, [user]);  // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    try {
      const [p, pr] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/producer/profile`, { headers: headers() }),
        axios.get(`${BACKEND_URL}/api/producer/projects`, { headers: headers() }),
      ]);
      setProfile({
        company_name: p.data.company_name || '',
        bio: p.data.bio || '',
        location: p.data.location || '',
        website: p.data.website || '',
        years_active: p.data.years_active || '',
      });
      setProjects(pr.data);
    } catch (err) {
      toast.error('Failed to load track record');
    } finally {
      setLoading(false);
    }
  };

  const saveProfile = async () => {
    const ya = (profile.years_active !== '' && profile.years_active != null) ? Number(profile.years_active) : undefined;
    const payload = {
      company_name: profile.company_name,
      bio: profile.bio,
      location: profile.location,
      website: profile.website,
      ...(ya !== undefined ? { years_active: ya } : {}),
    };
    try {
      await axios.put(`${BACKEND_URL}/api/producer/profile`, payload, { headers: headers() });
      toast.success('Profile saved');
    } catch (err) {
      toast.error('Save failed');
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyProject);
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      title: p.title || '',
      role: p.role || 'Producer',
      industry: p.industry || 'Bollywood',
      budget_inr: p.budget_inr || '',
      box_office_inr: p.box_office_inr || '',
      release_date: p.release_date || '',
      planned_release_date: p.planned_release_date || '',
      distribution: p.distribution || 'Theatrical',
      platform: p.platform || '',
      imdb_url: p.imdb_url || '',
      trailer_url: p.trailer_url || '',
      poster_url: p.poster_url || '',
      proof_doc_url: p.proof_doc_url || '',
      notes: p.notes || '',
    });
    setShowModal(true);
  };

  const submitProject = async (e) => {
    e.preventDefault();
    try {
      const budget = parseInrInput(form.budget_inr);
      const box = parseInrInput(form.box_office_inr);
      if (!Number.isFinite(budget) || budget <= 0) return toast.error('Enter a valid budget');
      if (!Number.isFinite(box) || box < 0) return toast.error('Enter a valid box office number');
      if (!form.title.trim()) return toast.error('Title required');
      if (!form.release_date) return toast.error('Release date required');

      const optionalKeys = ['planned_release_date', 'platform', 'imdb_url', 'trailer_url', 'poster_url', 'proof_doc_url', 'notes'];
      const base = {
        ...form,
        budget_inr: budget,
        box_office_inr: box,
      };
      const payload = Object.fromEntries(
        Object.entries(base).filter(([k, v]) => !(optionalKeys.includes(k) && !v))
      );

      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/producer/projects/${editingId}`, payload, { headers: headers() });
        toast.success('Project updated — submitted for re-verification');
      } else {
        await axios.post(`${BACKEND_URL}/api/producer/projects`, payload, { headers: headers() });
        toast.success('Project submitted for verification');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };

  const removeProject = async (id) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/producer/projects/${id}`, { headers: headers() });
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setDeleteTarget(null);
    }
  };

  const uploadProof = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/uploads/producer-asset?kind=doc`, fd, {
        headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, proof_doc_url: res.data.url }));
      toast.success('Proof uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="noise-overlay" />
      <nav data-web-chrome="nav" className="relative z-10 glass-card sticky top-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/producer-dashboard')} className="flex items-center gap-2 text-slate-300 hover:text-white" data-testid="back-to-dashboard">
            <ArrowLeft size={18} /> <span className="font-body text-sm">Back to dashboard</span>
          </button>
          {user && (
            <button onClick={() => navigate(`/producer/${user.id}`)} data-testid="view-public-profile" className="btn-secondary text-sm py-2">
              View public profile <ArrowSquareOut size={14} className="inline ml-1" />
            </button>
          )}
        </div>
      </nav>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-10">
          <h1 className="font-heading text-5xl font-bold mb-3">MY <span className="text-gold">TRACK RECORD</span></h1>
          <p className="font-body text-slate-400">Add your previous projects so investors can evaluate your work. Verified entries appear on your public profile.</p>
        </div>

        {/* Profile editor */}
        <section data-testid="profile-section" className="bg-card border border-white/10 rounded-lg p-6 mb-8">
          <h2 className="font-heading text-xl font-bold mb-4">Production house & bio</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="font-body text-xs text-slate-400">Production house / company</label>
              <input data-testid="profile-company" className="admin-input mt-1" value={profile.company_name} onChange={e => setProfile({ ...profile, company_name: e.target.value })} placeholder="e.g., Star Studios" />
            </div>
            <div>
              <label className="font-body text-xs text-slate-400">Location</label>
              <input data-testid="profile-location" className="admin-input mt-1" value={profile.location} onChange={e => setProfile({ ...profile, location: e.target.value })} placeholder="Mumbai, India" />
            </div>
            <div>
              <label className="font-body text-xs text-slate-400">Years active</label>
              <input data-testid="profile-years" type="number" min="0" max="80" className="admin-input mt-1" value={profile.years_active} onChange={e => setProfile({ ...profile, years_active: e.target.value })} placeholder="15" />
            </div>
            <div>
              <label className="font-body text-xs text-slate-400">Website</label>
              <input data-testid="profile-website" className="admin-input mt-1" value={profile.website} onChange={e => setProfile({ ...profile, website: e.target.value })} placeholder="https://yourstudio.com" />
            </div>
            <div className="md:col-span-2">
              <label className="font-body text-xs text-slate-400">Bio</label>
              <textarea data-testid="profile-bio" rows={3} className="admin-input mt-1" value={profile.bio} onChange={e => setProfile({ ...profile, bio: e.target.value })} placeholder="Award-winning producer with 15+ years across Bollywood and Tollywood..." />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button data-testid="save-profile-btn" onClick={saveProfile} className="btn-primary">Save profile</button>
          </div>
        </section>

        {/* Projects list */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold">Previous projects ({projects.length})</h2>
            <button data-testid="add-project-btn" onClick={openCreate} className="btn-primary flex items-center gap-2">
              <Plus size={16} weight="bold" /> Add project
            </button>
          </div>

          {loading ? (
            <div className="text-center py-10 text-slate-400 font-body">Loading...</div>
          ) : projects.length === 0 ? (
            <div className="bg-card border border-white/10 rounded-lg p-10 text-center">
              <p className="font-body text-slate-400">No projects yet. Add your first one to start building credibility with investors.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="projects-list">
              {projects.map(p => (
                <div key={p.id} className="bg-card border border-white/10 rounded-lg p-5" data-testid={`project-row-${p.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 flex-wrap mb-2">
                        <h3 className="font-heading text-lg font-bold">{p.title}</h3>
                        <span className="text-xs text-slate-400 font-body">{p.role} · {p.industry} · {p.distribution}{p.platform ? ` (${p.platform})` : ''}</span>
                        <StatusBadge status={p.status} />
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                        <div>
                          <div className="text-[10px] uppercase text-slate-400">Budget</div>
                          <div className="font-mono text-sm font-bold tabular-nums">₹{formatInrFriendly(p.budget_inr)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-slate-400">Box office</div>
                          <div className="font-mono text-sm font-bold tabular-nums">₹{formatInrFriendly(p.box_office_inr)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-slate-400">ROI</div>
                          <div className={`font-mono text-sm font-bold tabular-nums ${p.roi_pct > 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {p.roi_pct > 0 ? '+' : ''}{p.roi_pct}%
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase text-slate-400">Release</div>
                          <div className="font-mono text-sm">{p.release_date} {p.on_time === true ? '✓ on time' : p.on_time === false ? '⏱ delayed' : ''}</div>
                        </div>
                      </div>
                      {p.review_notes && (
                        <p className="mt-2 text-xs text-slate-400 italic">Admin note: {p.review_notes}</p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button data-testid={`edit-project-${p.id}`} onClick={() => openEdit(p)} className="btn-secondary py-2 text-sm flex items-center gap-1">
                        <PencilSimple size={14} /> Edit
                      </button>
                      <button aria-label="Delete project" data-testid={`delete-project-${p.id}`} onClick={() => setDeleteTarget(p.id)} className="py-2 px-3 text-sm rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20 transition-colors">
                        <Trash size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Add/Edit modal */}
      {showModal && (
        <Modal onClose={() => setShowModal(false)} title={editingId ? 'Edit project' : 'Add previous project'}>
          <form onSubmit={submitProject} data-testid="project-form" className="bg-card border border-white/10 rounded-lg max-w-3xl w-full max-h-[92vh] overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-2xl font-bold">{editingId ? 'Edit project' : 'Add previous project'}</h3>
              <button type="button" onClick={() => setShowModal(false)} className="text-slate-400 hover:text-white">✕</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="font-body text-xs text-slate-400">Title *</label>
                <input data-testid="form-title" required className="admin-input mt-1" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">Your role *</label>
                <select data-testid="form-role" className="admin-input mt-1" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">Industry *</label>
                <select data-testid="form-industry" className="admin-input mt-1" value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })}>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">Budget (INR) *</label>
                <input data-testid="form-budget" required className="admin-input mt-1" value={form.budget_inr} onChange={e => setForm({ ...form, budget_inr: e.target.value })} placeholder="e.g., 15 crore or 150000000" />
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">Box office gross (INR) *</label>
                <input data-testid="form-box" required className="admin-input mt-1" value={form.box_office_inr} onChange={e => setForm({ ...form, box_office_inr: e.target.value })} placeholder="e.g., 42 crore" />
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">Release date *</label>
                <input data-testid="form-release" type="date" required className="admin-input mt-1" value={form.release_date} onChange={e => setForm({ ...form, release_date: e.target.value })} />
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">Planned release date (for on-time tracking)</label>
                <input data-testid="form-planned-release" type="date" className="admin-input mt-1" value={form.planned_release_date} onChange={e => setForm({ ...form, planned_release_date: e.target.value })} />
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">Distribution *</label>
                <select data-testid="form-distribution" className="admin-input mt-1" value={form.distribution} onChange={e => setForm({ ...form, distribution: e.target.value })}>
                  {DISTRIBUTION.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">Platform (Netflix, PVR, Prime…)</label>
                <input data-testid="form-platform" className="admin-input mt-1" value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })} />
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">IMDb URL</label>
                <input data-testid="form-imdb" className="admin-input mt-1" value={form.imdb_url} onChange={e => setForm({ ...form, imdb_url: e.target.value })} placeholder="https://imdb.com/..." />
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">Trailer URL</label>
                <input data-testid="form-trailer" className="admin-input mt-1" value={form.trailer_url} onChange={e => setForm({ ...form, trailer_url: e.target.value })} placeholder="https://youtube.com/..." />
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">Poster URL</label>
                <input data-testid="form-poster" className="admin-input mt-1" value={form.poster_url} onChange={e => setForm({ ...form, poster_url: e.target.value })} />
              </div>
              <div>
                <label className="font-body text-xs text-slate-400">Proof document (PDF/image, max 15MB)</label>
                <div className="mt-1 flex items-center gap-2">
                  <input data-testid="form-proof-upload" type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => uploadProof(e.target.files?.[0])} className="text-xs flex-1" />
                  {uploading && <span className="text-xs text-slate-400">Uploading…</span>}
                </div>
                {form.proof_doc_url && (
                  <a href={`${BACKEND_URL}${form.proof_doc_url}`} target="_blank" rel="noreferrer" className="text-xs text-gold hover:underline mt-1 inline-block" data-testid="proof-uploaded-link">
                    View uploaded proof <ArrowSquareOut size={10} className="inline" />
                  </a>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="font-body text-xs text-slate-400">Notes / context for the admin reviewer</label>
                <textarea data-testid="form-notes" rows={2} className="admin-input mt-1" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" onClick={() => setShowModal(false)} className="btn-secondary text-sm py-2">Cancel</button>
              <button data-testid="form-submit" type="submit" className="btn-primary text-sm py-2">{editingId ? 'Save changes' : 'Submit for verification'}</button>
            </div>
          </form>
        </Modal>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this project?</AlertDialogTitle>
            <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => removeProject(deleteTarget)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProducerTrackRecord;
